import { Input, DatePicker, Select as AntSelect, Switch } from "antd";
import searchIcon from "../../../../assets/icons/search-icon.png";
import profilePic from "../../../../assets/images/image.jpg";
import crossIcon from "../../../../assets/icons/cross-Icon.png";
import { Select, Button1, Radio, TagInput } from "../../../index";
import { useEffect, useMemo, useRef, useState } from "react";
import DomesticTransferReview from "./DomesticTransferReview";
import {
  bankAccountValidationApi,
  beneficiariesApi,
  bsbApi,
  scheduledTransfersApi,
  transfersApi,
} from "../../../../api/endpoints";
import { newIdempotencyKey } from "../../../../utils/idempotency";
import { useBeneficiaries } from "../../../../data/live";
import TransferOtpModal from "../../../modals/TransferOtpModal";

const CATEGORIES = ["Family", "Friend", "Vendor", "Employee", "Business"];
const digitsOnly = (value) => String(value || "").replace(/\D/g, "");

const validationColor = (result) => {
  if (!result) return "#7D7D7D";
  if (!result.canProceed) return "#FC555B";
  return result.warning ? "#B7791F" : "#1bbb6b";
};

const validationFromError = (e) => {
  const result = e.response?.data?.validation || e.response?.data;
  if (result?.status && result?.message) return result;
  return {
    canProceed: false,
    status: "unable_to_confirm",
    message: "We could not confirm the account name match.",
  };
};

const DomesticTransfer = ({ sourceAccountId, sourceAccount }) => {
  const [step, setStep] = useState(0);
  const { beneficiaries, refresh: refreshBenes } = useBeneficiaries();

  const [isScheduled, setIsScheduled] = useState(false);
  const [transferCurrency, setTransferCurrency] = useState(sourceAccount?.currency || "AUD");

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedRecipientId, setSelectedRecipientId] = useState(null);

  const [form, setForm] = useState({
    sourceAmount: "",
    transferDate: null,
    accountType: "INDIVIDUAL",
    accountName: "",
    sortCode: "",
    iban: "",
    saveAsBeneficiary: true,
    category: "",
    referenceNote: "",
    scheduleDate: null,
    scheduleRepeat: null,
    scheduleEndDate: null,
  });
  const [quote, setQuote] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [otpOpen, setOtpOpen] = useState(false);

  // Pre-flight BSB validation: resolve the bank as soon as a 6-digit BSB is
  // entered, so a typo is caught before the transfer (and money) moves.
  const [bsbCheck, setBsbCheck] = useState({ loading: false, result: null });
  const [accountCheck, setAccountCheck] = useState({
    loading: false,
    result: null,
    key: "",
  });
  useEffect(() => {
    const code = digitsOnly(form.sortCode);
    if (code.length !== 6) {
      setBsbCheck({ loading: false, result: null });
      return;
    }
    let cancelled = false;
    setBsbCheck({ loading: true, result: null });
    const t = setTimeout(() => {
      bsbApi
        .lookup(code)
        .then((r) => !cancelled && setBsbCheck({ loading: false, result: r }))
        .catch(() => !cancelled && setBsbCheck({ loading: false, result: null }));
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [form.sortCode]);

  const auAccountPayload = useMemo(
    () => ({
      accountName: String(form.accountName || "").trim(),
      bsb: digitsOnly(form.sortCode),
      accountNumber: digitsOnly(form.iban),
    }),
    [form.accountName, form.sortCode, form.iban]
  );
  const auAccountKey = `${auAccountPayload.accountName}|${auAccountPayload.bsb}|${auAccountPayload.accountNumber}`;

  const runAuAccountValidation = async () => {
    setAccountCheck({ loading: true, result: null, key: auAccountKey });
    try {
      const result = await bankAccountValidationApi.validateAustralia(auAccountPayload);
      setAccountCheck({ loading: false, result, key: auAccountKey });
      return result;
    } catch (e) {
      const result = validationFromError(e);
      setAccountCheck({ loading: false, result, key: auAccountKey });
      return result;
    }
  };

  useEffect(() => {
    if (
      !auAccountPayload.accountName ||
      auAccountPayload.bsb.length !== 6 ||
      auAccountPayload.accountNumber.length < 5 ||
      auAccountPayload.accountNumber.length > 12 ||
      bsbCheck.result?.valid === false
    ) {
      setAccountCheck({ loading: false, result: null, key: auAccountKey });
      return;
    }
    let cancelled = false;
    setAccountCheck({ loading: true, result: null, key: auAccountKey });
    const t = setTimeout(() => {
      bankAccountValidationApi
        .validateAustralia(auAccountPayload)
        .then((result) => {
          if (!cancelled) setAccountCheck({ loading: false, result, key: auAccountKey });
        })
        .catch((e) => {
          if (!cancelled) {
            setAccountCheck({
              loading: false,
              result: validationFromError(e),
              key: auAccountKey,
            });
          }
        });
    }, 500);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [auAccountPayload, auAccountKey, bsbCheck.result?.valid]);
  const [idempotencyKey] = useState(newIdempotencyKey);

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  useEffect(() => {
    setTransferCurrency(sourceAccount?.currency || "AUD");
  }, [sourceAccount?.currency]);

  const filtered = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return [];
    return beneficiaries.filter(
      (b) =>
        (b.fullName || "").toLowerCase().includes(q) ||
        (b.businessName || "").toLowerCase().includes(q) ||
        (b.email || "").toLowerCase().includes(q) ||
        (b.mobile || "").toLowerCase().includes(q)
    );
  }, [beneficiaries, searchTerm]);

  const selectedRecipient = beneficiaries.find(
    (b) => b.id === selectedRecipientId
  );

  useEffect(() => {
    if (selectedRecipient) {
      update(
        "accountName",
        selectedRecipient.fullName || selectedRecipient.businessName || ""
      );
      update(
        "iban",
        selectedRecipient.bankAccounts?.[0]?.accountNumber ||
          selectedRecipient.bankAccounts?.[0]?.iban ||
          ""
      );
      update("sortCode", selectedRecipient.bankAccounts?.[0]?.sortCode || "");
    }
  }, [selectedRecipient]);

  const debounceRef = useRef(null);
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (
      !sourceAccountId ||
      !form.sourceAmount ||
      isNaN(Number(form.sourceAmount)) ||
      Number(form.sourceAmount) <= 0
    ) {
      setQuote(null);
      return;
    }
    debounceRef.current = setTimeout(() => {
      transfersApi
        .quote({
          sourceAccountId,
          sourceAmount: Number(form.sourceAmount),
          destCurrency: transferCurrency,
          transferType: "DOMESTIC",
        })
        .then(setQuote)
        .catch(() => setQuote(null));
    }, 350);
    return () => clearTimeout(debounceRef.current);
  }, [sourceAccountId, form.sourceAmount, transferCurrency]);

  // Confirm button: scheduled transfers go straight through (they are
  // executed later by the worker); immediate transfers require the emailed
  // OTP first, so we open the verification modal instead.
  const handleConfirm = () => {
    setError(null);
    if (!sourceAccountId) {
      setError("Please select a source account first");
      return;
    }
    if (!form.sourceAmount || Number(form.sourceAmount) <= 0) {
      setError("Enter a valid amount");
      return;
    }
    if (isScheduled && form.scheduleDate) {
      handleCreate();
    } else {
      setOtpOpen(true);
    }
  };

  const handleContinue = async () => {
    setError(null);
    if (!auAccountPayload.accountName) {
      setError("Account name is required.");
      return;
    }
    if (auAccountPayload.bsb.length !== 6) {
      setError("BSB must be 6 digits.");
      return;
    }
    if (
      auAccountPayload.accountNumber.length < 5 ||
      auAccountPayload.accountNumber.length > 12
    ) {
      setError("Account number must be 5 to 12 digits.");
      return;
    }

    const result =
      accountCheck.result && accountCheck.key === auAccountKey
        ? accountCheck.result
        : await runAuAccountValidation();
    if (!result?.canProceed) {
      setError(result?.message || "Please enter valid Australian bank account details.");
      return;
    }
    setStep(1);
  };

  const handleCreate = async ({ otpCode, pinCode } = {}) => {
    setError(null);
    setSubmitting(true);
    try {
      let beneficiaryId = selectedRecipientId;
      if (!beneficiaryId && form.saveAsBeneficiary && form.accountName) {
        const isBusiness = form.accountType === "BUSINESS";
        const created = await beneficiariesApi.add({
          accountType: form.accountType,
          fullName: !isBusiness ? form.accountName : undefined,
          businessName: isBusiness ? form.accountName : undefined,
          paymentType: "LOCAL",
          payoutMethod: "BANK_ACCOUNT",
          bankAccount: {
            iban: form.iban || undefined,
            accountNumber: form.iban || undefined,
            sortCode: form.sortCode || undefined,
            routingNumber: form.sortCode || undefined,
            currency: transferCurrency,
          },
        });
        beneficiaryId = created.id;
        refreshBenes();
      }

      if (isScheduled && form.scheduleDate) {
        await scheduledTransfersApi.create({
          sourceAccountId,
          sourceAmount: Number(form.sourceAmount),
          destCurrency: transferCurrency,
          transferType: "DOMESTIC",
          beneficiaryId,
          category: form.category || undefined,
          referenceNote: form.referenceNote || undefined,
          startDate: form.scheduleDate,
          frequency: "MONTHLY",
          endDate: form.scheduleEndDate || undefined,
        });
      } else {
        await transfersApi.create({
          sourceAccountId,
          sourceAmount: Number(form.sourceAmount),
          destCurrency: transferCurrency,
          transferType: "DOMESTIC",
          beneficiaryId,
          category: form.category || undefined,
          referenceNote: form.referenceNote || undefined,
          otpCode,
          pinCode,
        }, idempotencyKey);
      }
      setOtpOpen(false);
      setStep(2);
    } catch (e) {
      // OTP/PIN problems stay inside the modal so the user can retry/resend.
      const apiErr = e.response?.data?.error || "";
      if (apiErr.startsWith("OTP_") || apiErr.startsWith("PIN_")) throw e;
      setOtpOpen(false);
      setError(
        e.response?.data?.message ||
          e.response?.data?.error ||
          e.response?.data?.details?.[0]?.message ||
          e.message ||
          "Transfer failed"
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (step === 1) {
    return (
      <>
        <DomesticTransferReview
          sourceAccount={sourceAccount}
          form={form}
          quote={quote}
          recipient={selectedRecipient}
          submitting={submitting}
          error={error}
          onConfirm={handleConfirm}
          onClick={setStep}
        />
        <TransferOtpModal
          open={otpOpen}
          onClose={() => setOtpOpen(false)}
          onVerify={handleCreate}
        />
      </>
    );
  }

  if (step === 2) {
    return (
      <div className="col-12 p-5 d-flex flex-column align-items-center gap-3">
        <span className="fs-3 fw-bold" style={{ color: "#1bbb6b" }}>
          ✓ Transfer {isScheduled ? "scheduled" : "submitted"}
        </span>
        <span className="fs14px text-muted">
          Domestic transfer of {form.sourceAmount}{" "}
          {transferCurrency} processed.
        </span>
        <Button1
          buttonName={"View transactions"}
          className="btn-dark"
          onClick={() => (window.location.href = "/my-transaction")}
        />
      </div>
    );
  }

  return (
    <>
      <div className="col-lg-5 p-3">
        <div className="d-flex flex-column gap-3">
          <span className="fs-5 fw-medium">Domestic Transfer</span>

          <div className="d-flex flex-column gap-2">
            <div className="d-flex gap-3 ">
              <div className="d-flex flex-column gap-2 w-75 ">
                <label className="fs12px">Amount you want to send</label>
                <Input
                  className="p-3 w-100"
                  placeholder="0.00"
                  value={form.sourceAmount}
                  onChange={(e) => update("sourceAmount", e.target.value)}
                />
              </div>
              <div className="d-flex flex-column gap-2 new-transfer-currency-field">
                <label className="fs12px">Currency</label>
                <AntSelect
                  value={transferCurrency}
                  onChange={setTransferCurrency}
                  options={["AUD", "USD", "EUR", "GBP", "PKR"].map((currency) => ({
                    label: currency,
                    value: currency,
                  }))}
                  className="new-transfer-currency-select"
                  style={{ width: "100%" }}
                  popupClassName="new-transfer-currency-popup"
                />
              </div>
            </div>

            <span className="fs12px" style={{ color: "#7D7D7D" }}>
              Available balance :{" "}
              <span className="text-black">
                {sourceAccount
                  ? `${Number(sourceAccount.availableBalance).toLocaleString()} ${sourceAccount.currency}`
                  : "0.00"}
              </span>
            </span>
          </div>

          <div className="d-flex flex-column gap-2">
            <label className="fs12px">Transfer date</label>
            <DatePicker
              placeholder="Select date"
              className="p-3"
              onChange={(d) =>
                update("transferDate", d?.toISOString() || null)
              }
            />

            <span className="fs12px" style={{ color: "#7D7D7D" }}>
              Usually arrives within 0-1 business day
            </span>
          </div>

          <div className=" d-flex flex-column gap-2">
            <span className="fs12px">Recipients Type</span>
            <div className="d-flex justify-content-between  ">
              <div className="d-flex gap-2 align-items-center w-50">
                <Radio
                  name={"dom-account-type"}
                  accentColor={"green"}
                  height={"20px"}
                  width={"20px"}
                  checked={form.accountType === "BUSINESS"}
                  onClick={() => update("accountType", "BUSINESS")}
                />
                <span className="fs14px">Business</span>
              </div>
              <div className="d-flex gap-2 align-items-center w-50 justify-content-end justify-content-md-start">
                <Radio
                  name={"dom-account-type"}
                  accentColor={"green"}
                  height={"20px"}
                  width={"20px"}
                  checked={form.accountType === "INDIVIDUAL"}
                  onClick={() => update("accountType", "INDIVIDUAL")}
                />
                <span className="fs14px">Individual</span>
              </div>
            </div>
          </div>

          <div className="d-flex flex-column gap-3">
            <Input
              placeholder="Search through Email or Phone"
              className="p-3"
              style={{ borderColor: "#1bbb6b" }}
              prefix={<img src={searchIcon} alt="search" width={18} />}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              allowClear
            />
            <div
              className="d-flex flex-column gap-3 overflow-auto"
              style={{ maxHeight: "150px" }}
            >
              {filtered.length === 0 && searchTerm && (
                <span className="fs12px text-muted">
                  No matching recipients
                </span>
              )}
              {filtered.map((b) => (
                <div
                  key={b.id}
                  className="d-flex justify-content-between align-items-center cursor-pointer"
                  onClick={() => {
                    setSelectedRecipientId(b.id);
                    setSearchTerm("");
                  }}
                >
                  <div className="d-flex gap-2 ">
                    <div className="rounded-circle">
                      <img
                        className="rounded-circle"
                        src={profilePic}
                        alt="user-img"
                        width={40}
                        height={40}
                      />
                    </div>
                    <div className="d-flex flex-column ">
                      <span className="fs14px fw-medium">
                        {b.fullName || b.businessName}
                      </span>
                      <span className="fs12px" style={{ color: "#7D7D7D" }}>
                        {b.email || b.mobile || ""}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
              {selectedRecipient && (
                <div className="d-flex justify-content-between align-items-center">
                  <div className="d-flex gap-2 ">
                    <div className="rounded-circle">
                      <img
                        className="rounded-circle"
                        src={profilePic}
                        alt="user-img"
                        width={40}
                        height={40}
                      />
                    </div>
                    <div className="d-flex flex-column ">
                      <span className="fs14px fw-medium">
                        {selectedRecipient.fullName ||
                          selectedRecipient.businessName}
                      </span>
                      <span className="fs12px" style={{ color: "#7D7D7D" }}>
                        Selected
                      </span>
                    </div>
                  </div>
                  <div>
                    <img
                      src={crossIcon}
                      alt="cross"
                      className="cursor-pointer"
                      onClick={() => setSelectedRecipientId(null)}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="col-lg-6 p-3">
        <div className="d-flex flex-column gap-3">
          <span className="fs-5 fw-medium">Recipients Informations</span>

          <div className="d-flex flex-column gap-2">
            <label className="fs12px">Account Name</label>
            <Input
              className="p-3"
              placeholder="Recipient's name"
              value={form.accountName}
              onChange={(e) => update("accountName", e.target.value)}
            />
          </div>

          <div className="d-flex flex-column gap-2">
            <label className="fs12px">BSB/Routing/Sort Code</label>
            <Input
              className="p-3"
              placeholder="XXXXXX"
              value={form.sortCode}
              onChange={(e) => update("sortCode", e.target.value)}
            />
            {bsbCheck.loading ? (
              <span className="fs12px" style={{ color: "#7D7D7D" }}>
                Checking BSB...
              </span>
            ) : bsbCheck.result?.valid ? (
              <span className="fs12px fw-medium" style={{ color: "#1bbb6b" }}>
                {bsbCheck.result.bank}
                {bsbCheck.result.branch ? ` (${bsbCheck.result.branch})` : ""}
              </span>
            ) : bsbCheck.result && !bsbCheck.result.valid ? (
              <span className="fs12px fw-medium" style={{ color: "#FC555B" }}>
                {bsbCheck.result.reason}
              </span>
            ) : (
              <span className="fs12px" style={{ color: "#7D7D7D" }}>
                6-digit Bank-State-Branch number
              </span>
            )}
          </div>

          <div className="d-flex flex-column gap-2">
            <label className="fs12px">IBAN/Account number</label>
            <Input
              className="p-3"
              placeholder="XXXXXXXX"
              value={form.iban}
              onChange={(e) => update("iban", e.target.value)}
            />
            {accountCheck.loading ? (
              <span className="fs12px" style={{ color: "#7D7D7D" }}>
                Confirming account details...
              </span>
            ) : accountCheck.result ? (
              <span
                className="fs12px fw-medium"
                style={{ color: validationColor(accountCheck.result) }}
              >
                {accountCheck.result.message}
              </span>
            ) : (
              <span className="fs12px" style={{ color: "#7D7D7D" }}>
                {bsbCheck.result && !bsbCheck.result.valid
                  ? "Enter a valid BSB to verify the account."
                  : "Enter account name, BSB and account number to verify."}
              </span>
            )}
          </div>

          <div className="d-flex flex-column gap-2 mt-4">
            <div className="d-flex justify-content-between align-items-center">
              <span className="fs-5 fw-bold">
                Schedule or set recurring transfer
              </span>
              <Switch
                onChange={setIsScheduled}
                checked={isScheduled}
              />
            </div>

            {isScheduled && (
              <div className="d-flex flex-column gap-3 mt-4">
                <div className="d-flex flex-column gap-2">
                  <label className="fs12px">Date</label>
                  <DatePicker
                    placeholder="Pick start date"
                    className="p-3"
                    onChange={(d) =>
                      update("scheduleDate", d?.toISOString() || null)
                    }
                  />
                </div>
                <div className="d-flex flex-column gap-2">
                  <label className="fs12px">Repeat</label>
                  <Select
                    value={form.scheduleRepeat}
                    options={[
                      { name: "Weekly", value: "WEEKLY" },
                      { name: "Monthly", value: "MONTHLY" },
                      { name: "Yearly", value: "YEARLY" },
                    ]}
                    defaultValue="Choose frequency"
                    className="p-3"
                    onChange={(e) =>
                      update("scheduleRepeat", e.target.value)
                    }
                  />
                </div>
                <div className="d-flex flex-column gap-2">
                  <label className="fs12px">End date</label>
                  <DatePicker
                    placeholder="Pick end date"
                    className="p-3"
                    onChange={(d) =>
                      update("scheduleEndDate", d?.toISOString() || null)
                    }
                  />
                </div>
              </div>
            )}
          </div>

          <div className="d-flex flex-column gap-3 mt-3">
            <span className="fs-5 fw-bold">Additional Details</span>
            <div className="d-flex flex-column gap-2 ">
              <label className="fs12px ">Category</label>
              <Select
                value={form.category}
                options={CATEGORIES.map((c) => ({ name: c, value: c }))}
                defaultValue="Choose Category"
                className="rounded-2 p-3"
                onChange={(e) => update("category", e.target.value)}
              />
            </div>
            <div className="d-flex flex-column gap-2 ">
              <label className="fs12px ">Reference</label>
              <Input
                className="p-3"
                placeholder="Reference"
                value={form.referenceNote}
                onChange={(e) => update("referenceNote", e.target.value)}
              />
            </div>
            <div className="d-flex flex-column gap-2 ">
              <label className="fs12px ">Tags</label>
              <TagInput
                className="border py-3 "
                inputBgColor="transparent"
                tagColor="#1bbb6b"
                tagTextColor="#000"
                limit={10}
                placeholder="Add up to 10 Tags"
              />
            </div>
          </div>
        </div>
      </div>

      {error && (
        <div className="col-12 p-2">
          <div className="fs12px" style={{ color: "#FC555B" }}>
            {error}
          </div>
        </div>
      )}

      <div className=" d-flex justify-content-md-end justify-content-between p-2 gap-3">
        <Button1
          buttonName={"Save as Draft"}
          className={"btn-outline-success"}
        />
        <Button1
          buttonName={"Continue"}
          className="btn-dark "
          onClick={handleContinue}
        />
      </div>
    </>
  );
};

export default DomesticTransfer;
